import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { FetchComponentsReason } from '../common/constants/fetch-components-reason.tsx';
import type { Component, CreatedComponent } from '../common/model/types.tsx';
import type { SetAction as AnalyticsAction } from './analytics/actions.tsx';
import type { SetAction as ContextAction } from './context/actions.tsx';
import type { ComponentValidationError } from './errors.tsx';
import type { Action as ProjectAction } from './project/actions.tsx';
import type { Action as QueryAction } from './query/actions.tsx';
import type { Action as UIAction } from './ui/actions.tsx';

// add success

export const ADD_COMPONENT_SUCCESS = 'root.ADD_COMPONENT_SUCCESS' as const;
export type AddComponentSuccessAction = {
	type: typeof ADD_COMPONENT_SUCCESS;
	payload: CreatedComponent;
};
export const addComponentSuccess = (payload: CreatedComponent): AddComponentSuccessAction => ({
	type: ADD_COMPONENT_SUCCESS,
	payload,
});

// add fail

export const ADD_COMPONENT_FAIL = 'root.ADD_COMPONENT_FAIL' as const;
export type AddComponentFailAction = {
	type: typeof ADD_COMPONENT_FAIL;
};
export const addComponentFail = (): AddComponentFailAction => ({
	type: ADD_COMPONENT_FAIL,
});

// update start

export const INITIATE_UPDATE_COMPONENT = 'root.INITIATE_UPDATE_COMPONENT' as const;
export type InitiateUpdateComponentPayload = {
	id: string;
};
export type InitiateUpdateComponentAction = {
	type: typeof INITIATE_UPDATE_COMPONENT;
	payload: InitiateUpdateComponentPayload;
};
export const initiateUpdateComponent = (
	payload: InitiateUpdateComponentPayload,
): InitiateUpdateComponentAction => ({
	type: INITIATE_UPDATE_COMPONENT,
	payload,
});

// update start success

export const INITIATE_UPDATE_COMPONENT_SUCCESS = 'root.INITIATE_UPDATE_COMPONENT_SUCCESS' as const;
export type InitiateUpdateComponentSuccessPayload = {
	id: string;
	component: Component;
};
export type InitiateUpdateComponentSuccessAction = {
	type: typeof INITIATE_UPDATE_COMPONENT_SUCCESS;
	payload: InitiateUpdateComponentSuccessPayload;
};
export const initiateUpdateComponentSuccess = (
	payload: InitiateUpdateComponentSuccessPayload,
): InitiateUpdateComponentSuccessAction => ({
	type: INITIATE_UPDATE_COMPONENT_SUCCESS,
	payload,
});

// update start fail

export const INITIATE_UPDATE_COMPONENT_FAIL = 'root.INITIATE_UPDATE_COMPONENT_FAIL' as const;
export type InitiateUpdateComponentFailPayload = {
	id: string;
	error: FetchError;
};
export type InitiateUpdateComponentFailAction = {
	type: typeof INITIATE_UPDATE_COMPONENT_FAIL;
	payload: InitiateUpdateComponentFailPayload;
};
export const initiateUpdateComponentFail = (
	payload: InitiateUpdateComponentFailPayload,
): InitiateUpdateComponentFailAction => ({
	type: INITIATE_UPDATE_COMPONENT_FAIL,
	payload,
});

// update start

export const UPDATE_COMPONENT_REQUEST = 'root.UPDATE_COMPONENT_REQUEST' as const;
export type UpdateComponentRequestAction = {
	type: typeof UPDATE_COMPONENT_REQUEST;
};
export const updateComponentRequest = (): UpdateComponentRequestAction => ({
	type: UPDATE_COMPONENT_REQUEST,
});

// patch component

export const PATCH_COMPONENT = 'root.PATCH_COMPONENT' as const;
export type PatchComponentPayload = {
	id: string;
	component: Partial<Component>;
};
export type PatchComponentAction = {
	type: typeof PATCH_COMPONENT;
	payload: PatchComponentPayload;
};
export const patchComponent = (payload: PatchComponentPayload): PatchComponentAction => ({
	type: PATCH_COMPONENT,
	payload,
});

// update success

export const UPDATE_COMPONENT_SUCCESS = 'root.UPDATE_COMPONENT_SUCCESS' as const;
export type UpdateComponentSuccessPayload = {
	componentName: string;
};
export type UpdateComponentSuccessAction = {
	type: typeof UPDATE_COMPONENT_SUCCESS;
	payload: UpdateComponentSuccessPayload;
};
export const updateComponentSuccess = (
	payload: UpdateComponentSuccessPayload,
): UpdateComponentSuccessAction => ({
	type: UPDATE_COMPONENT_SUCCESS,
	payload,
});

// update fail

export const UPDATE_COMPONENT_FAIL = 'root.UPDATE_COMPONENT_FAIL' as const;
// eslint-disable-next-line jira/export/restrict-types-re-export
export type UpdateComponentFailPayload = FetchError;
export type UpdateComponentFailAction = {
	type: typeof UPDATE_COMPONENT_FAIL;
	payload: UpdateComponentFailPayload;
};
export const updateComponentFail = (
	payload: UpdateComponentFailPayload,
): UpdateComponentFailAction => ({
	type: UPDATE_COMPONENT_FAIL,
	payload,
});

// update validation fail

export const UPDATE_COMPONENT_VALIDATION_FAIL = 'root.UPDATE_COMPONENT_VALIDATION_FAIL' as const;
// eslint-disable-next-line jira/export/restrict-types-re-export
export type UpdateComponentValidationFailPayload = ComponentValidationError;
export type UpdateComponentValidationFailAction = {
	type: typeof UPDATE_COMPONENT_VALIDATION_FAIL;
	payload: UpdateComponentValidationFailPayload;
};
export const updateComponentValidationFail = (
	payload: UpdateComponentValidationFailPayload,
): UpdateComponentValidationFailAction => ({
	type: UPDATE_COMPONENT_VALIDATION_FAIL,
	payload,
});

// get components page

export const GET_COMPONENTS_PAGE_REQUEST = 'components.GET_COMPONENTS_PAGE_REQUEST' as const;
export type GetComponentsPageRequestPayload = {
	page: number;
	initiatedBy: FetchComponentsReason;
};
export type GetComponentsPageRequestAction = {
	type: typeof GET_COMPONENTS_PAGE_REQUEST;
	payload: GetComponentsPageRequestPayload;
};
export const getComponentsPageRequest = (
	payload: GetComponentsPageRequestPayload,
): GetComponentsPageRequestAction => ({
	type: GET_COMPONENTS_PAGE_REQUEST,
	payload,
});

// set components page

export const SET_COMPONENTS_PAGE = 'components.SET_COMPONENTS_PAGE' as const;
export type SetComponentsPagePayload = {
	byId: {
		[key: string]: Component;
	};
	list: string[];
	listAri: string[];
	total: number;
};
export type SetComponentsPageAction = {
	type: typeof SET_COMPONENTS_PAGE;
	payload: SetComponentsPagePayload;
};
export const setComponentsPage = (payload: SetComponentsPagePayload): SetComponentsPageAction => ({
	type: SET_COMPONENTS_PAGE,
	payload,
});

// delete success

export const DELETE_COMPONENT_SUCCESS = 'components.DELETE_COMPONENT_SUCCESS' as const;
export type DeleteComponentSuccessPayload = {
	id: string;
	component: Component;
};
export type DeleteComponentSuccessAction = {
	type: typeof DELETE_COMPONENT_SUCCESS;
	payload: DeleteComponentSuccessPayload;
};
export const deleteComponentSuccess = (
	payload: DeleteComponentSuccessPayload,
): DeleteComponentSuccessAction => ({
	type: DELETE_COMPONENT_SUCCESS,
	payload,
});

// delete fail

export const DELETE_COMPONENT_FAILURE = 'components.DELETE_COMPONENT_FAILURE' as const;
export type DeleteComponentFailurePayload = {
	id: string;
	error: FetchError;
	isDialogOpen: boolean;
};
export type DeleteComponentFailureAction = {
	type: typeof DELETE_COMPONENT_FAILURE;
	payload: DeleteComponentFailurePayload;
};
export const deleteComponentFailure = (
	payload: DeleteComponentFailurePayload,
): DeleteComponentFailureAction => ({
	type: DELETE_COMPONENT_FAILURE,
	payload,
});

export type Action =
	| AddComponentSuccessAction
	| AddComponentFailAction
	| InitiateUpdateComponentAction
	| InitiateUpdateComponentSuccessAction
	| InitiateUpdateComponentFailAction
	| UpdateComponentRequestAction
	| PatchComponentAction
	| UpdateComponentSuccessAction
	| UpdateComponentFailAction
	| UpdateComponentValidationFailAction
	| GetComponentsPageRequestAction
	| SetComponentsPageAction
	| DeleteComponentSuccessAction
	| DeleteComponentFailureAction
	| AnalyticsAction
	| ContextAction
	| ProjectAction
	| QueryAction
	| UIAction;
